import "./itemCard.scss";

import PropTypes from "prop-types";
import React, { PureComponent } from "react";
import ImmutablePropTypes from "react-immutable-proptypes";

import { ITEM_CARD_MAX_INLINE_IDENTIFIERS } from "../../constants";
import Dimensions, { scalePixelsToFontSize } from "../../constants/styles/dimensions";
import { calcItemCardDateLineRelated } from "../../features/itemCard/itemCardFunctions";
import { typeSelectors } from "../../modules/definition/types";
import { PropTypeShapes } from "../../types";
import { getMetricsLabelSize } from "../../utils/labelUtils";
import { withCharacterContext } from "../character/CharacterContext";
import FormEditableLabel from "../form/FormEditableLabel";
import Avatar from "../interface/Avatar";
import Icon from "../interface/Icon";
import Identifier from "../interface/Identifier";
import Image from "../interface/Image";

const { MAX_ITEM_TEXT_WIDTH } = Dimensions;

const renderTextLines = lines => {
  return lines.map((l, index) => {
    return <span key={index}>{l}</span>;
  });
};

const renderRelatedItems = (relatedItemsByType, showRelationshipTypes, typesById) => {
  let children = null;

  if (relatedItemsByType && relatedItemsByType.size > 0) {
    // TODO: order of types
    children = relatedItemsByType
      .filter(listOfType => showRelationshipTypes.includes(listOfType.typeId))
      .map(listOfType => {
        let list = [];
        const { idDisplayList, typeId } = listOfType;
        if (idDisplayList.size > 0) {
          const type = typesById.get(typeId);
          const { icon: typeIcon, color: typeColor } = type;
          // Type icon
          if (typeIcon) {
            list.push(<Icon icon={typeIcon} key={`type-${typeId}`} color={typeColor} />);
          }
          list = list.concat(
            idDisplayList.map(idDisplay =>
              idDisplay.isAvatar ? (
                <Avatar key={idDisplay.id} color={idDisplay.color} image={idDisplay.image} initials={idDisplay.label} />
              ) : (
                <Identifier key={idDisplay.id} {...idDisplay.toJS()} color="iconFade" />
              )
            )
          );
        }
        const idsOfType =
          list.length > 0 ? (
            <div className="identifiersOfType" key={typeId}>
              {list}
            </div>
          ) : null;
        return idsOfType;
      });
  }
  return children;
};

const renderBlocksList = (items, icon) => {
  let children = null;
  if (items && items.size > 0) {
    children = items.slice(0, ITEM_CARD_MAX_INLINE_IDENTIFIERS).map((v, index) => {
      return <Identifier key={index} {...v.toJS()} />;
    });
    if (items.size > ITEM_CARD_MAX_INLINE_IDENTIFIERS) {
      children = children.push(
        <span key="remainder" className="avatarRemainder">
          ... + {`${items.size - ITEM_CARD_MAX_INLINE_IDENTIFIERS}`} more
        </span>
      );
    }
    // The icon
    children = children.unshift(<Icon icon={icon} key={`blockIcon-${icon}`} />);
  }
  return <div className="itemCardBlocksList">{children}</div>;
};

class ItemCard extends PureComponent {
  constructor(props) {
    super(props);
    this.getTextSizeFunction = this.getTextSizeFunction.bind(this);
  }

  componentDidMount() {
    const { onChangeEditing } = this.props;
    if (onChangeEditing) {
      onChangeEditing(this.getInitialEditing());
    }
  }

  getInitialEditing() {
    let result = false;
    //#!if iOS !== true
    result =
      this.props.isActiveView &&
      this.props.autoEditWhenBlank &&
      !this.props.isPreview &&
      this.props.isSelected &&
      !this.props.label;
    //#!endif
    return result;
  }

  getTextSizeFunction(text) {
    return getMetricsLabelSize(
      text,
      this.props.characterContext.getWidthMap(),
      this.props.characterContext.getFontSize()
    );
  }

  /**
   * Here the logic should match that in itemCardFunction calcItemCardSize()
   * Normally put the date and duration on the same row.
   * If the total width exceeds the maximum width of item card,
   * split them into two rows
   */
  getDateLine() {
    const { rect, dateTextSize, durationTextSize: origDurationTextSize, characterContext } = this.props;

    const fontSize = characterContext.getFontSize();
    const widthMap = characterContext.getWidthMap();

    const showDates = this.props.showDates && dateTextSize.text;
    const showDuration = this.props.showDuration && origDurationTextSize.text;

    const SCALED_MAX_ITEM_TEXT_WIDTH = scalePixelsToFontSize(MAX_ITEM_TEXT_WIDTH, fontSize);

    const rectWidth = rect ? rect.width : SCALED_MAX_ITEM_TEXT_WIDTH;

    const { durationTextSize, isDateDurationSplitLine } = calcItemCardDateLineRelated(
      showDates,
      showDuration,
      dateTextSize,
      origDurationTextSize,
      fontSize,
      widthMap,
      rectWidth
    );

    return !isDateDurationSplitLine
      ? showDates && showDuration
        ? `${dateTextSize.text} (${durationTextSize.text})`
        : showDates
          ? dateTextSize.text
          : showDuration
            ? durationTextSize.text
            : ""
      : [
        showDates ? <div key="dateText">{dateTextSize.text}</div> : null,
        showDuration ? (
          <div key="durationText">{`${showDates ? "(" : ""}${durationTextSize.text}${showDates ? ")" : ""}`}</div>
        ) : null
      ];
  }

  render() {
    const {
      rect,
      dateTextSize,
      durationTextSize,
      titleMetrics,
      label,
      showNarrative,
      narrativeTextSize,
      showIdentifier,
      showRelationshipTypes,
      showBlocks,
      showBlockedBy,
      identifier,
      relatedItemsByType,
      blockingIdentifiers,
      blockedByIdentifiers,
      showSummary,
      summaryMetrics,
      showImage,
      imagePath,
      onChangeEditing,
      typesById,
      type
    } = this.props;

    const style = rect
      ? {
        left: `${rect.left}px`,
        top: `${rect.top}px`,
        width: `${rect.width}px`,
        height: `${rect.height}px`
      }
      : {};

    if (style) {
      Object.assign(style, style);
    }

    const showDates = this.props.showDates && dateTextSize.text;
    const showDuration = this.props.showDuration && durationTextSize.text;

    const dateTextLine = this.getDateLine();

    const showRelationshipTypesBool = showRelationshipTypes && showRelationshipTypes.size > 0;

    const itemType = typesById.get(type);
    const displayLabel = typeSelectors.getTypeLabelDisplay(itemType);
    const placeholder = `${itemType.label} ${displayLabel}`;

    return (
      <div className="itemCard" style={style}>
        <div
          className={`itemCardContent ${this.props.hasViolatedConstraints ? "violatedConstraint" : ""} ${
            this.props.cardIsOnNewLine ? "cardIsOnNewLine" : ""
          }`}
        >
          {this.props.hasViolatedConstraints ? (
            <Icon className="statusIcon" icon="statusConflict" color="statusError" />
          ) : null}
          <div className="itemCardLabel">
            <FormEditableLabel
              value={label}
              displayValue={renderTextLines(titleMetrics.lines)}
              isEditing={this.getInitialEditing()}
              placeholder={placeholder}
              multiline={true}
              allowNewLine={false}
              onChange={this.props.onLabelChange}
              getSizeFunction={this.getTextSizeFunction}
              onChangeEditing={onChangeEditing}
              isStruckOut={this.props.isCompleted}
            />
          </div>
          {showDates || showDuration ? <div className="itemCardDate">{dateTextLine}</div> : null}
          {showNarrative && narrativeTextSize.text ? (
            <div className="itemCardNarrativeLabel">{narrativeTextSize.text}</div>
          ) : null}
          {showIdentifier ||
          showRelationshipTypesBool ||
          (showBlocks && blockingIdentifiers.size > 0) ||
          (showBlockedBy && blockedByIdentifiers.size > 0) ? (
              <div className="itemCardIdentifiersRow">
                {showIdentifier ? <Identifier label={identifier} color="iconFade" isAvatar={false} /> : null}
                {showRelationshipTypesBool
                  ? renderRelatedItems(relatedItemsByType, showRelationshipTypes, typesById)
                  : null}
                {showBlockedBy ? renderBlocksList(blockedByIdentifiers, "footerTimelineDependencyLinesBlockedBy") : null}
                {showBlocks ? renderBlocksList(blockingIdentifiers, "footerTimelineDependencyLinesBlocks") : null}
              </div>
            ) : null}
          {showSummary && summaryMetrics.size.height ? (
            <div className="itemCardSummary">{renderTextLines(summaryMetrics.lines)}</div>
          ) : null}
          {showImage && imagePath ? <Image source={imagePath} className="itemCardImage" /> : null}
        </div>
      </div>
    );
  }
}

ItemCard.propTypes = {
  // Any special instructions when placing the data?
  style: PropTypes.object,
  rect: PropTypeShapes.flatRect,

  label: PropTypes.string,
  isActiveView: PropTypes.bool,
  isSelected: PropTypes.bool,
  isPreview: PropTypes.bool,
  onLabelChange: PropTypes.func,
  onChangeEditing: PropTypes.func,
  autoEditWhenBlank: PropTypes.bool.isRequired,

  // Data to allow us to show things...
  titleMetrics: PropTypeShapes.textMetrics.isRequired,
  dateTextSize: PropTypeShapes.textSize.isRequired,
  durationTextSize: PropTypeShapes.textSize.isRequired,
  narrativeTextSize: PropTypeShapes.textSize.isRequired,
  summaryMetrics: PropTypeShapes.textMetrics.isRequired,
  identifierSize: PropTypeShapes.size.isRequired,
  identifier: PropTypes.string.isRequired,
  relatedItemsByType: ImmutablePropTypes.list.isRequired,
  blockingIdentifiers: PropTypeShapes.identifierList,
  blockedByIdentifiers: PropTypeShapes.identifierList,

  imagePath: PropTypes.string,
  hasViolatedConstraints: PropTypes.bool,
  cardIsOnNewLine: PropTypes.bool,
  isCompleted: PropTypes.bool,

  // What properties should we show...?
  showIdentifier: PropTypes.bool,
  showDates: PropTypes.bool,
  showDuration: PropTypes.bool,
  showNarrative: PropTypes.bool,
  showRelationshipTypes: ImmutablePropTypes.listOf(PropTypes.string),
  showBlocks: PropTypes.bool,
  showBlockedBy: PropTypes.bool,
  showImage: PropTypes.bool,
  showSummary: PropTypes.bool,
  showInfoIcons: PropTypes.bool,

  typesById: ImmutablePropTypes.map.isRequired,
  type: PropTypes.string.isRequired,

  // From character context
  characterContext: PropTypes.object.isRequired
};

export default withCharacterContext(ItemCard);
